import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

export default function Calendar({ role, staffId }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [markedDates, setMarkedDates] = useState({})

  useEffect(() => {
    fetchMonthAppointments()
  }, [currentMonth])

  useEffect(() => {
    fetchAppointmentsForDate(selectedDate)
  }, [selectedDate])

  async function fetchMonthAppointments() {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const start = new Date(year, month, 1).toISOString().split('T')[0]
    const end = new Date(year, month + 1, 0).toISOString().split('T')[0]

    let query = supabase
      .from('appointments')
      .select('appointment_date, status')
      .gte('appointment_date', start)
      .lte('appointment_date', end)
      .neq('status', 'cancelled')

    if (role === 'provider' && staffId) query = query.eq('staff_id', staffId)

    const { data } = await query
    if (data) {
      const marks = {}
      data.forEach(apt => { marks[apt.appointment_date] = true })
      setMarkedDates(marks)
    }
  }

  async function fetchAppointmentsForDate(date) {
    setLoading(true)
    let query = supabase
      .from('appointments')
      .select(`*, services (name, duration, price), profiles!appointments_customer_id_fkey (full_name, phone, email), staff (full_name)`)
      .eq('appointment_date', date)
      .order('start_time')

    if (role === 'provider' && staffId) query = query.eq('staff_id', staffId)

    const { data } = await query
    if (data) setAppointments(data)
    setLoading(false)
  }

  function getDaysInMonth(date) {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days = []
    for (let i = 0; i < firstDay; i++) days.push(null)
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i))
    }
    return days
  }

  function getStatusColor(status) {
    switch (status) {
      case 'confirmed': return '#D4AF77'
      case 'completed': return '#4A754A'
      case 'cancelled': return '#ef4444'
      case 'no_show': return '#f59e0b'
      default: return '#888'
    }
  }

  const days = getDaysInMonth(currentMonth)
  const today = new Date().toISOString().split('T')[0]

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Calendar</h1>

      <div style={styles.layout}>
        {/* Calendar */}
        <div style={styles.calendarCard}>
          <div style={styles.calendarHeader}>
            <button
              style={styles.navBtn}
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
              ←
            </button>
            <span style={styles.monthLabel}>
              {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>
            <button
              style={styles.navBtn}
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
              →
            </button>
          </div>

          <div style={styles.dayHeaders}>
            {DAYS.map(d => (
              <div key={d} style={styles.dayHeader}>{d}</div>
            ))}
          </div>

          <div style={styles.daysGrid}>
            {days.map((day, i) => {
              if (!day) return <div key={`empty-${i}`} />
              const dateStr = day.toISOString().split('T')[0]
              const isSelected = dateStr === selectedDate
              const isToday = dateStr === today
              const hasApts = markedDates[dateStr]

              return (
                <button
                  key={dateStr}
                  style={{
                    ...styles.dayBtn,
                    ...(isSelected ? styles.dayBtnSelected : {}),
                    ...(isToday && !isSelected ? styles.dayBtnToday : {}),
                  }}
                  onClick={() => setSelectedDate(dateStr)}>
                  {day.getDate()}
                  {hasApts && <div style={styles.dot} />}
                </button>
              )
            })}
          </div>
        </div>

        {/* Appointments */}
        <div style={styles.appointmentsPanel}>
          <h2 style={styles.dateTitle}>
            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
              weekday: 'long', month: 'long', day: 'numeric'
            })}
          </h2>

          {loading ? (
            <div style={styles.empty}>Loading...</div>
          ) : appointments.length === 0 ? (
            <div style={styles.empty}>No appointments this day</div>
          ) : (
            <div style={styles.aptList}>
              {appointments.map(apt => (
                <div key={apt.id} style={styles.aptCard}>
                  <div style={styles.aptTime}>{apt.start_time}</div>
                  <div style={styles.aptInfo}>
                    <div style={styles.aptClient}>{apt.profiles?.full_name}</div>
                    <div style={styles.aptService}>{apt.services?.name}</div>
                    {apt.profiles?.phone && (
                      <div style={styles.aptPhone}>{apt.profiles.phone}</div>
                    )}
                    {role !== 'provider' && (
                      <div style={styles.aptStaff}>✂️ {apt.staff?.full_name}</div>
                    )}
                  </div>
                  <div style={styles.aptRight}>
                    <div style={styles.aptPrice}>${apt.services?.price}</div>
                    <div style={{
                      ...styles.aptStatus,
                      color: getStatusColor(apt.status),
                      backgroundColor: getStatusColor(apt.status) + '20',
                    }}>
                      {apt.status}
                    </div>
                    {apt.card_last_four && (
                      <div style={styles.cardOnFile}>
                        💳 ···· {apt.card_last_four}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: { padding: '32px', maxWidth: '1200px' },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#D4AF77',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginBottom: '24px',
    fontFamily: 'Cormorant Garamond, serif',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '340px 1fr',
    gap: '24px',
    alignItems: 'start',
  },
  calendarCard: {
    backgroundColor: '#111',
    border: '1px solid #1a1a1a',
    borderRadius: '2px',
    padding: '20px',
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  navBtn: {
    background: 'none',
    border: '1px solid #222',
    color: '#D4AF77',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    borderRadius: '2px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  monthLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#F5F0E8',
    letterSpacing: '0.5px',
  },
  dayHeaders: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    marginBottom: '8px',
  },
  dayHeader: {
    fontSize: '10px',
    color: '#444',
    letterSpacing: '1px',
    textAlign: 'center',
    padding: '4px 0',
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '2px',
  },
  dayBtn: {
    position: 'relative',
    height: '36px',
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: '13px',
    cursor: 'pointer',
    borderRadius: '2px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    transition: 'background 0.2s',
  },
  dayBtnSelected: {
    backgroundColor: '#D4AF77',
    color: '#000',
    fontWeight: '600',
  },
  dayBtnToday: {
    color: '#D4AF77',
    fontWeight: '600',
  },
  dot: {
    width: '3px',
    height: '3px',
    borderRadius: '50%',
    backgroundColor: '#D4AF77',
  },
  appointmentsPanel: {
    backgroundColor: '#111',
    border: '1px solid #1a1a1a',
    borderRadius: '2px',
    padding: '20px',
    minHeight: '400px',
  },
  dateTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#F5F0E8',
    marginBottom: '16px',
    letterSpacing: '0.3px',
  },
  empty: {
    color: '#444',
    fontSize: '14px',
    padding: '24px',
    textAlign: 'center',
  },
  aptList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  aptCard: {
    display: 'flex',
    gap: '16px',
    padding: '14px',
    backgroundColor: '#0a0a0a',
    border: '1px solid #1a1a1a',
    borderRadius: '2px',
    alignItems: 'flex-start',
  },
  aptTime: {
    fontSize: '13px',
    color: '#D4AF77',
    fontWeight: '600',
    width: '70px',
    flexShrink: 0,
    paddingTop: '2px',
  },
  aptInfo: { flex: 1 },
  aptClient: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#F5F0E8',
    marginBottom: '2px',
  },
  aptService: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '2px',
  },
  aptPhone: {
    fontSize: '12px',
    color: '#444',
    marginBottom: '2px',
  },
  aptStaff: {
    fontSize: '12px',
    color: '#444',
  },
  aptRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  aptPrice: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#D4AF77',
  },
  aptStatus: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '2px 8px',
    borderRadius: '2px',
  },
  cardOnFile: {
    fontSize: '11px',
    color: '#4A754A',
  },
}